import React, { useEffect, useState } from "react";
import axios from "../../../../utils/axios";
import { useSelector } from "react-redux";
import ReactHtmlParser from "react-html-parser";
import { useSnackbar } from "notistack";
import StaticPopupNew from "components/StaticPopupNew";
import EditPoPopup from "./EditPoPopup";
import WarningPopup from "components/WarningPopup";
import StaticPopup from "components/StaticPopup";

const AddPoTable = ({
  rfqData,
  rfqId,
  poData = [],
  setAddPoData,
  setPurchaseOrderCount,
  poLoading,
  poError,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const auth = useSelector((state) => state.auth);
  const user = auth?.user;

  const [openUpdatePo, setOpenUpdatePo] = useState(false);
  const [editPo, setEditPo] = useState(null);
  const [localPoData, setLocalPoData] = useState(poData);
  const [poToDelete, setPoToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openRemarksPopup, setOpenRemarksPopup] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState("");

  const sortedPoData = [...localPoData].sort((a, b) => {
    const dateA = new Date(a.createdOn || 0);
    const dateB = new Date(b.createdOn || 0);
    return dateB - dateA; // For descending order (newest first)
  });

  useEffect(() => {
    setLocalPoData(poData); // Update when poData prop changes
  }, [poData]);

  const handleEditPO = (po) => {
    setEditPo(po);
    setOpenUpdatePo(true);
  };

  const closeUpdatePo = () => {
    setEditPo(null);
    setOpenUpdatePo(false);
  };

  const openDeleteWarning = (po) => {
    setPoToDelete(po);
  };

  const closeDeleteWarning = () => {
    setPoToDelete(null);
  };

  const handleShowRemarks = (remarks) => {
    setSelectedRemarks(remarks || "N/A");
    setOpenRemarksPopup(true);
  };

  const closeRemarksPopup = () => {
    setOpenRemarksPopup(false);
    setSelectedRemarks("");
  };

  const handleConfirmDeletePO = async () => {
    if (!poToDelete) return;

    try {
      setDeleteLoading(true);

      const payload = {
        rfqId,
        supplierId: poToDelete.supplierId,
        poNumber: poToDelete.poNumber,
      };

      const response = await axios.delete("/api/rfqs/delete/po", {
        data: payload,
      });

      if (response.data.error) {
        enqueueSnackbar(response.data.message || "Failed to delete PO", {
          variant: "error",
        });
      } else {
        enqueueSnackbar("PO deleted successfully", { variant: "success" });

        const updatedPoList = localPoData.filter(
          (item) => item.poNumber !== poToDelete.poNumber
        );
        setLocalPoData(updatedPoList); // update local state
        setAddPoData(updatedPoList);
        setPurchaseOrderCount(updatedPoList?.length);
      }
    } catch (err) {
      console.error("Error deleting PO:", err);
      enqueueSnackbar("Server error while deleting PO", { variant: "error" });
    } finally {
      setDeleteLoading(false);
      closeDeleteWarning();
    }
  };

  if (poLoading) {
    return (
      <div className="flex justify-center items-center h-[100vh]">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {sortedPoData.length === 0 ? (
        <div
          style={{ height: "calc(100vh - 67px)" }}
          className="flex justify-center items-center w-full"
        >
          <div className="flex flex-col items-center justify-center">
            <img
              src="https://storagereponeevaydevcdn.blob.core.windows.net/business/Search_image.png"
              alt="Search Image"
              className="h-[116px] w-[122px] mb-6"
            />
            <p className="text-md font-bold text-[#3C3C3C]">
              {`No ${
                rfqData?.rfqType === "material"
                  ? "Purchase Orders"
                  : "Work Orders"
              } Added`}
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4 pb-8">
          {sortedPoData.map((po, index) => (
            <div
              key={po.poNumber}
              className="border rounded-md bg-white p-4 shadow-sm"
            >
              {/* HEADER */}
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  Sr# : {index + 1}
                </h3>

                <div className="flex gap-4">
                  <button type="button" onClick={() => handleEditPO(po)}>
                    <img
                      src="https://storagereponeevaydevcdn.blob.core.windows.net/business/pencil_gray_create_rfq.svg"
                      alt="Edit"
                      className="h-4 w-4"
                    />
                  </button>

                  <button
                    type="button"
                    onClick={() => openDeleteWarning(po)}
                    disabled={
                      deleteLoading && poToDelete?.poNumber === po.poNumber
                    }
                  >
                    {deleteLoading && poToDelete?.poNumber === po.poNumber ? (
                      <div className="loader h-4 w-4 border-2 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="18"
                      >
                        <path
                          fill="#4B5563"
                          d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zM19 4h-3.5l-1-1h-5l-1 1H5v2h14z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* GRID WRAPPER */}
              <div className="grid grid-cols-3 gap-x-4">
                <div className="text-xs col-span-2">
                  {/* ROW 1 — PO DETAILS */}
                  <div className=" grid md:grid-cols-8 sm:grid-cols-3 text-xs gap-x-2 gap-y-3">
                    <div className="col-span-1">
                      <span className="text-gray-500">PO No.</span>
                      <p className="font-medium">{po.poNumber}</p>
                    </div>
                    <div className="col-span-1">
                      <span className="text-gray-500">PO Amount</span>
                      <p className="font-medium">
                        Rs. {Number(po.poAmount || 0).toLocaleString("en-IN")}
                      </p>
                    </div>


                    <div className="col-span-3">
                      <span className="text-gray-500">Vendor Name</span>
                      <p className="font-medium">{po.vendorName}</p>
                    </div>
                    <div className="col-span-1">
                      <span className="text-gray-500">Created On</span>
                      <p className="font-medium">
                        {po.createdOn
                          ? new Date(po.createdOn).toLocaleDateString("en-GB")
                          : "-"}
                      </p>
                    </div>


                    <div className="col-span-2">
                      <span className="text-gray-500">Created By</span>
                      <p className="font-medium">{po.createdByName}</p>
                    </div>
                  </div>

                  {/* ROW 2 — GRN TABLE (FIXED POSITION) */}
                  {po.grnDetails?.some(
                    (grn) => grn.grnNumber || grn.grnDate || grn.grnComments
                  ) && (
                    <div className="md:col-span-8 row-start-2 mt-2">
                      <h4 className="text-xs font-semibold text-gray-600 mb-2">
                        GRN Details
                      </h4>

                      <div className="border rounded overflow-hidden">
                        <table className="w-full text-xs border-collapse">
                          <thead className="bg-gray-100 text-gray-600">
                            <tr>
                              <th className="border px-2 py-1 text-left">
                                Sr#
                              </th>
                              <th className="border p-1 text-left">
                                GRN No.
                              </th>
                              <th className="border px-2 py-1 text-center">
                                GRN Date
                              </th>
                              <th className="border px-2 py-1 text-left">
                                GRN Comments
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {po.grnDetails.map((grn, i) => (
                              <tr key={grn._id}>
                                <td className="border px-2 py-1">{i + 1}</td>
                                <td className="border px-2 py-1">
                                  {grn.grnNumber}
                                </td>
                                <td className="border px-2 py-1 text-center">
                                  {grn.grnDate
                                    ? new Date(grn.grnDate).toLocaleDateString(
                                        "en-GB"
                                      )
                                    : "-"}
                                </td>
                                <td className="border px-2 py-1">
                                  {ReactHtmlParser(grn.grnComments || "-")}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
                {/* ROW 1 — REMARKS */}
                <div className="md:col-span-1">
                  <span className="text-gray-500 text-xs">Remarks</span>
                  <div className=" text-xs overflow-auto">
                    {ReactHtmlParser(po.remarks || "-")}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit PO Modal */}
      <StaticPopupNew
        header={"Update PO"}
        bodyStyle="pb-0 pt-0"
        openModal={openUpdatePo}
        handleClose={closeUpdatePo}
        width="6xl"
      >
        {editPo && (
          <EditPoPopup
            po={editPo}
            rfqId={rfqId}
            onUpdateSuccess={(updatedPo) => {
              // Update localPoData with new one
              const updatedList = localPoData.map((p) =>
                p.poNumber === updatedPo.poNumber ? updatedPo : p
              );
              setLocalPoData(updatedList); // local update
              setAddPoData(updatedList);
              setPurchaseOrderCount(updatedList?.length);

              closeUpdatePo(); // Close modal
            }}
          />
        )}
      </StaticPopupNew>

      {/* Delete Warning Popup */}
      <WarningPopup
        openModal={!!poToDelete}
        handleClosePopup={closeDeleteWarning}
        handleSubmit={handleConfirmDeletePO}
        warningText={`Are you sure you want to delete PO: ${poToDelete?.poNumber}?`}
        loading={deleteLoading}
      />

      {/* Remarks Popup */}
      <StaticPopup
        openModal={openRemarksPopup}
        handleClose={closeRemarksPopup}
        width={"xl"}
        bodyStyle="pb-0 pt-0"
        className="px-0"
        header={"Remarks"}
      >
        <div className="py-4">
          <p>{ReactHtmlParser(selectedRemarks)}</p>
        </div>
      </StaticPopup>
    </>
  );
};

export default AddPoTable;
